// Day Table layout — the density redesign's default view (replaces the old card Grid).
// One HTML table: CLINIC | AM | PM, hairlines as the ONLY border system (a horizontal rule
// under every row, vertical rules between the three columns — no cell cards, no chip
// boxes). People are text lines, counts are bare numerals, and a Mon–Sat week strip under
// the table shows the week's booked load. Rows are clinics or employees — the page
// precomputes the row model.
import { useState } from "react";
import { useDroppable } from "@dnd-kit/core";
import { Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  CellPicker,
  CountNumeral,
  GapLine,
  IdentityAvatar,
  PersonLine,
  clinicPickerOptions,
  staffPickerOptions,
} from "./planner-bits";
import { identityTone } from "./work-plan-utils";

function DayTableCell({ row, session, mode, data, canManage, onAssign, actions }) {
  const cell = row.cells[session.key];
  const { setNodeRef, isOver } = useDroppable({
    id: `cell:${row.id}|${session.key}`,
    data: { rowId: row.id, session: session.key },
    disabled: row.locked || !canManage,
  });
  const [pickerOpen, setPickerOpen] = useState(false);
  const isEmpty = cell.items.length === 0 && cell.gaps.length === 0;

  const excludeIds = cell.items.map((entry) => (mode === "clinic" ? entry.personId : entry.clinicId));
  const pickerOptions =
    mode === "clinic"
      ? staffPickerOptions(data.staff, excludeIds, data.roleView)
      : clinicPickerOptions(data.clinics, excludeIds);

  return (
    <td
      ref={setNodeRef}
      className={cn(
        "border-b border-border px-3 py-[9px] align-top transition-colors duration-120",
        session.borderRight && "border-r",
        isOver && "bg-primary/5 outline-dashed outline-1 -outline-offset-2 outline-primary",
      )}
    >
      <div className="flex items-start gap-2.5">
        <div className="flex min-w-0 flex-1 flex-col">
          {cell.items.map((entry) => (
            <PersonLine
              key={entry.key}
              entry={entry}
              session={session}
              canManage={canManage}
              actions={actions}
              flagDefs={data.flagDefs}
            />
          ))}
          {cell.gaps.map((gap) => (
            <GapLine
              key={gap.id}
              whoName={gap.whoName}
              isDoctor={gap.whoIsDoctor}
              canManage={canManage}
              onFindCover={() => setPickerOpen(true)}
            />
          ))}
          {isEmpty ? (
            <span className="flex h-[26px] items-center text-xs text-muted-foreground/60">
              {row.locked ? "—" : mode === "clinic" ? "— no staff assigned" : "— not assigned"}
            </span>
          ) : null}
          {/* The add affordance is a ghost: invisible until the row is hovered (or the
              picker is open), so a fully-staffed table carries zero standing chrome. */}
          {!row.locked && canManage ? (
            <CellPicker
              mode={mode}
              options={pickerOptions}
              open={pickerOpen}
              onOpenChange={setPickerOpen}
              onPick={(pickedId) => {
                setPickerOpen(false);
                onAssign(row, session.key, pickedId);
              }}
            >
              <button
                type="button"
                title={mode === "clinic" ? "Assign support staff" : "Add a clinic"}
                className={cn(
                  "-mx-1.5 flex h-6 w-9 items-center justify-center rounded-[7px] text-muted-foreground/70 opacity-0 transition-opacity duration-120 hover:bg-muted hover:text-foreground focus-visible:opacity-100 data-[state=open]:opacity-100 group-hover/row:opacity-100",
                )}
              >
                <Plus className="size-3.5" />
              </button>
            </CellPicker>
          ) : null}
        </div>
        {typeof cell.apptCount === "number" ? (
          <CountNumeral count={cell.apptCount} className="mt-[5px]" />
        ) : null}
      </div>
    </td>
  );
}

function RowLabelCell({ row, dayTotal, maxDayTotal }) {
  return (
    <td className="w-[236px] border-b border-r border-border px-3 py-[9px] align-top">
      {row.person ? (
        <div className="flex items-center gap-2" title={row.locked ? `${row.sub} · from Sessions — read-only here` : row.sub}>
          <IdentityAvatar id={row.id} name={row.title} photoUrl={row.photoUrl} size={20} />
          <span
            className={cn(
              "min-w-0 truncate text-[12.5px] leading-tight",
              row.locked ? "font-normal text-muted-foreground" : "font-bold",
            )}
          >
            {row.title}
          </span>
          <span className="shrink-0 truncate text-[11px] text-muted-foreground">{row.sub}</span>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2" title={row.sub || undefined}>
            <span
              className="h-3.5 w-[3px] shrink-0 rounded-full"
              style={{ background: identityTone(row.id).solid }}
            />
            <span className="min-w-0 truncate text-[12.5px] font-bold leading-tight">{row.title}</span>
          </div>
          {typeof dayTotal === "number" ? (
            <div className="mt-[5px] flex items-center gap-[7px] pl-[11px]">
              <CountNumeral
                count={dayTotal}
                title={`${dayTotal} appointment${dayTotal === 1 ? "" : "s"} booked across the day`}
              />
              <span className="h-1 max-w-24 flex-1 overflow-hidden rounded-full bg-muted">
                <span
                  className="block h-full rounded-full"
                  style={{
                    width: `${maxDayTotal > 0 ? Math.round((dayTotal / maxDayTotal) * 100) : 0}%`,
                    background: "oklch(0.55 0.09 235)",
                  }}
                />
              </span>
            </div>
          ) : null}
        </>
      )}
    </td>
  );
}

/**
 * The Mon–Sat week strip: each tile shows how STAFFED a day is — assigned support staff
 * as a fraction of the whole available roster (`capacity`), NOT of the week's busiest
 * day, so a full bar always means "everyone is placed" and a half-empty week reads
 * half-empty instead of quietly renormalising to look done. The shown day is
 * highlighted; every tile navigates.
 */
export function WeekStrip({ days, currentDate, capacity, onNavigate }) {
  if (!days?.length) return null;
  const denominator = Math.max(Number(capacity) || 0, 1);
  return (
    <div className="mt-4 flex items-center gap-2.5 rounded-[11px] border border-dashed border-muted-foreground/40 px-3.5 py-3">
      <span className="shrink-0 text-[11px] font-black uppercase tracking-[0.1em] text-muted-foreground">
        Week
      </span>
      <div className="grid flex-1 grid-cols-6 gap-1.5">
        {days.map((day) => {
          const isCurrent = day.date === currentDate;
          return (
            <button
              key={day.date}
              type="button"
              onClick={() => onNavigate(day.date)}
              title={`${day.total || 0} of ${denominator} support staff assigned — open this day`}
              className={cn(
                "rounded-[7px] px-2 py-1.5 text-left transition-colors duration-120",
                isCurrent
                  ? "bg-primary/25 dark:bg-[oklch(0.32_0.05_248)]"
                  : "bg-muted hover:bg-muted/70",
              )}
            >
              <span
                className={cn(
                  "block text-[10.5px] font-black tracking-wide",
                  isCurrent ? "text-foreground" : "text-muted-foreground",
                )}
              >
                {day.label}
              </span>
              <span className="mt-[5px] block h-[3px] overflow-hidden rounded-full">
                <span
                  className="block h-full rounded-full"
                  style={{
                    width: `${Math.min(100, Math.round(((day.total || 0) / denominator) * 100))}%`,
                    background: "oklch(0.5 0.08 235)",
                  }}
                />
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function DayTablePlanner({
  rows,
  sessions,
  mode,
  data,
  appointmentCounts,
  canManage,
  onAssign,
  actions,
  week,
  weekCapacity,
  onNavigateDate,
  currentDate,
}) {
  // Vertical hairlines separate the three columns: the label column and every session but
  // the last carry a right border.
  const sessionCols = sessions.map((session, index) => ({
    ...session,
    borderRight: index < sessions.length - 1,
  }));
  const dayTotals =
    mode === "clinic" && appointmentCounts
      ? Object.fromEntries(
          rows.map((row) => [
            row.id,
            sessions.reduce((sum, session) => sum + (row.cells[session.key]?.apptCount || 0), 0),
          ]),
        )
      : null;
  const maxDayTotal = dayTotals ? Math.max(...Object.values(dayTotals), 0) : 0;

  return (
    <div>
      <table className="w-full table-fixed border-separate border-spacing-0 font-normal">
        <colgroup>
          <col className="w-[236px]" />
          {sessionCols.map((session) => (
            <col key={session.key} />
          ))}
        </colgroup>
        <thead>
          <tr>
            <th className="sticky top-0 z-[5] border-b border-border bg-background px-3 pb-2 pt-4 text-left text-[10.5px] font-black uppercase tracking-[0.1em] text-muted-foreground">
              {mode === "clinic" ? "Clinic" : "Employee"}
            </th>
            {sessionCols.map((session) => (
              <th
                key={session.key}
                className="sticky top-0 z-[5] border-b border-border bg-background px-3 pb-2 pt-4 text-left text-[10.5px] font-black uppercase tracking-[0.1em] text-muted-foreground"
              >
                {session.label}
                <span className="ml-1.5 text-[11px] font-normal normal-case tracking-normal opacity-80">
                  {session.start}–{session.end}
                  {appointmentCounts
                    ? ` · ${appointmentCounts[session.key] || 0} appts`
                    : ""}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className="group/row">
              <RowLabelCell
                row={{ ...row, person: mode !== "clinic" }}
                dayTotal={dayTotals ? dayTotals[row.id] : undefined}
                maxDayTotal={maxDayTotal}
              />
              {sessionCols.map((session) => (
                <DayTableCell
                  key={session.key}
                  row={row}
                  session={session}
                  mode={mode}
                  data={data}
                  canManage={canManage}
                  onAssign={onAssign}
                  actions={actions}
                />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {week ? (
        <WeekStrip days={week} currentDate={currentDate} capacity={weekCapacity} onNavigate={onNavigateDate} />
      ) : null}
    </div>
  );
}
